import { ExpandMore } from '@mui/icons-material';
// Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormLabel,
  TextField,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FC, useEffect, useState } from 'react';
import { IMaterialsDelivered } from '../../../models';
import { formatMoney } from '../../../helpers';

interface IMobileMaterialsDelivered {
  original: IMaterialsDelivered;
  setMaterialsDelivered: (val) => void;
  materialsDelivered: IMaterialsDelivered[];
  isNumberOnly?: boolean;
  isDisabled?: boolean;
}

export const MobileMaterialsDelivered: FC<IMobileMaterialsDelivered> = ({
  original,
  setMaterialsDelivered,
  materialsDelivered,
  isNumberOnly,
  isDisabled
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(true);
  const { description, unit, quantityOrdered, quantityReceived, materialsDeliveredId, unitPrice } =
    original;

  const [qtyReceived, setQtyReceived] = useState<string | number>(quantityReceived);

  const handleAccordionChange = (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setIsOpen(isExpanded);
  };

  useEffect(() => {
    setMaterialsDelivered(old =>
      old.map((item, index) => {
        if (item.materialsDeliveredId === materialsDeliveredId) {
          return {
            ...item,
            quantityReceived: qtyReceived
          };
        }
        return item;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qtyReceived]);

  return (
    <Accordion className={classes.root} expanded={isOpen} onChange={handleAccordionChange}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography variant="body2">
            <span className={clsx(classes.boldName, classes.subLabel)}>{description}</span>
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Description:
          </FormLabel>
          <Typography component="span" variant="body2">
            {description || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Unit:
          </FormLabel>
          <Typography component="span" variant="body2">
            {unit || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Price:
          </FormLabel>
          <Typography component="span" variant="body2">
            {unitPrice ? formatMoney(unitPrice) : '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Qty Ordered:
          </FormLabel>
          <Typography component="span" variant="body2">
            {quantityOrdered || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Qty Received:
          </FormLabel>
          {isDisabled ? (
            <Typography component="span" variant="body2">
              {qtyReceived}
            </Typography>
          ) : (
            <TextField
              fullWidth
              variant="outlined"
              autoComplete="nope"
              label=""
              name="quantity"
              size="small"
              value={qtyReceived}
              onChange={e => {
                if (isNumberOnly) {
                  const re = /^[0-9\b]+$/;
                  // make sure it is number only before we update state
                  if (e.target.value === '' || re.test(e.target.value)) {
                    setQtyReceived(e.target.value);
                  }
                } else {
                  setQtyReceived(e.target.value);
                }
              }}
            />
          )}
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    marginBottom: '0.5rem',
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  roleWrapper: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  undefinedPart: {
    color: theme.palette.error.main
  },
  fieldOrderId: {
    fontSize: '1rem',
    padding: 0,
    marginLeft: '-5px'
  }
}));
